import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { WalletService } from '../wallet.service';
import countries from '@shared/data/countries.json';
import currencies from '@shared/data/currencies.json';
import { BankAccount, Card } from '../wallet.model';
import { CommonModule } from '@angular/common';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { CardModule } from 'primeng/card';
import { SkeletonModule } from 'primeng/skeleton';
import { FluidModule } from 'primeng/fluid';
import { Subscription } from 'rxjs';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { RadioButtonModule } from 'primeng/radiobutton';
import { InputNumberModule } from 'primeng/inputnumber';

@Component({
  selector: 'app-bank-accounts-list',
  imports: [
    CommonModule,
    AngularSvgIconModule,
    ButtonModule,
    DividerModule,
    CardModule,
    SkeletonModule,
    FluidModule,
    ReactiveFormsModule,
    DialogModule,
    SelectModule,
    RadioButtonModule,
    FormsModule,
    InputNumberModule,
  ],
  templateUrl: './bank-accounts-list.component.html',
})
export class BankAccountsListComponent implements OnInit, OnDestroy {
  @Input() isStandalone: boolean = true;
  @Input() isSelectable: boolean = false;

  showAddBankAccountDialog: boolean = false;
  bankAccounts: BankAccount[] = [];
  selectedTransactionMethod: Card | BankAccount | null = null;
  readonly countries = countries;
  readonly currencies = currencies;
  readonly addNewBankAccountForm: FormGroup;

  private bankAccountsSub = Subscription.EMPTY;
  private selectedTransactionMethodSub = Subscription.EMPTY;

  readonly _walletService = inject(WalletService);
  private readonly _formBuilder = inject(FormBuilder);

  get fBankAccount() {
    return this.addNewBankAccountForm.controls;
  }

  constructor() {
    this.addNewBankAccountForm = this._formBuilder.group({
      accountName: [undefined, Validators.required],
      country: [undefined, Validators.required],
      currency: [undefined, Validators.required],
      bankName: [undefined, Validators.required],
      // branchName: [undefined, Validators.required],
      // accountNumber: [undefined, Validators.required],
      iban: [undefined, Validators.required],
      // swiftCode: [undefined, Validators.required],
    });

    this.currencies = this.currencies.map((currency) => ({
      ...currency,
      label: `${currency.name} - ${currency.code} (${currency.symbol})`,
    }));
  }

  ngOnInit(): void {
    this.bankAccountsSub = this._walletService.bankAccounts$.subscribe(
      (bankAccounts) => (this.bankAccounts = bankAccounts)
    );

    this.selectedTransactionMethodSub =
      this._walletService.selectedTransactionMethod$.subscribe((method) => {
        this.selectedTransactionMethod = method;
      });

    this._walletService.getBankAccounts();
  }

  onAddNewBankAccountDialog(): void {
    this.showAddBankAccountDialog = true;
    this.addNewBankAccountForm.reset();
  }

  onAddNewBankAccount(): void {
    if (this.addNewBankAccountForm.invalid) {
      return;
    }

    const fvalue = this.addNewBankAccountForm['value'];
    const bankAccount: BankAccount = {
      accountName: fvalue.accountName,
      country: fvalue.country.code,
      currency: fvalue.currency.code,
      bankName: fvalue.bankName,
      // branchName: fvalue.branchName,
      // accountNumber: fvalue.accountNumber,
      iban: fvalue.iban,
      // swiftCode: fvalue.swiftCode,
    };

    this._walletService.addNewBankAccount(bankAccount);
    this.showAddBankAccountDialog = false;
  }

  onBankAccountSelected(): void {
    this._walletService.setTransactionMethod(this.selectedTransactionMethod!);
  }

  onRemoveBankAccount(index: number): void {
    this._walletService.removeBankAccount(this.bankAccounts[index]);
  }

  ngOnDestroy(): void {
    this.bankAccountsSub.unsubscribe();
    this.selectedTransactionMethodSub.unsubscribe();
  }
}
